import { metadataService } from '@services/metadata.service';
import type { AnalyzeAudioResponse } from '@services/metadata.service';
import { UploadFileGroup } from '../types';

export type GroupAnalysisStatus = 'idle' | 'analyzing' | 'completed' | 'error';

export interface GroupAnalysisState {
  groupId: string;
  status: GroupAnalysisStatus;
  error?: string;
}

const MAX_CONCURRENT = 3;

function validateBpm(bpm: number | undefined): number | undefined {
  if (!bpm || bpm < 50 || bpm > 220) return undefined;
  return bpm;
}

async function analyzeGroup(
  group: UploadFileGroup,
  files: File[],
  userId: string
): Promise<{ groupId: string; bpm?: string; musicalKey?: string; error?: string }> {
  const mainFile = files.find((f) => f.name === group.mainFileName);
  if (!mainFile) {
    return { groupId: group.id, error: 'Main file not found' };
  }

  try {
    const result: AnalyzeAudioResponse = await metadataService.analyzeAudio(mainFile, userId);
    if (result.success && (result.bpm || result.key)) {
      const bpm = validateBpm(result.bpm);
      return {
        groupId: group.id,
        bpm: bpm ? String(bpm) : undefined,
        musicalKey: result.key?.trim() || undefined,
      };
    }
    return {
      groupId: group.id,
      error: result.error || result.message || 'Analysis failed',
    };
  } catch (err) {
    return {
      groupId: group.id,
      error: err instanceof Error ? err.message : 'Analysis failed',
    };
  }
}

/**
 * Analyze BPM/Key for multiple groups with limited concurrency.
 */
export async function analyzeGroupsInParallel(
  groups: UploadFileGroup[],
  files: File[],
  userId: string,
  onProgress?: (states: Map<string, GroupAnalysisState>) => void
): Promise<Map<string, { bpm?: string; musicalKey?: string; error?: string }>> {
  const results = new Map<string, { bpm?: string; musicalKey?: string; error?: string }>();
  const states = new Map<string, GroupAnalysisState>();
  groups.forEach((g) => states.set(g.id, { groupId: g.id, status: 'idle' }));

  const queue = [...groups];

  const runNext = async (): Promise<void> => {
    const group = queue.shift();
    if (!group) return;

    states.set(group.id, { groupId: group.id, status: 'analyzing' });
    onProgress?.(new Map(states));

    const result = await analyzeGroup(group, files, userId);
    results.set(group.id, result);

    if (result.error) {
      states.set(group.id, { groupId: group.id, status: 'error', error: result.error });
    } else {
      states.set(group.id, { groupId: group.id, status: 'completed' });
    }
    onProgress?.(new Map(states));

    await runNext();
  };

  const workers = Array.from({ length: Math.min(MAX_CONCURRENT, groups.length) }, () => runNext());
  await Promise.all(workers);

  return results;
}
